import {
  CaretDownFilled,
  CaretLeftFilled,
  DeleteOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { Button, Form, message, Switch, Tag, Upload } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { useState } from 'react'
import FileItem from '../../../components/Common/FileItemComponent/FileItem'
import ContentBlockWrapper from '../../../components/ContentBlockWrapper'
import RichEditor from '../../../components/RichEditor/RichEditor'
import utils from '../../../utils'
import { renderElementToPdfFile } from '../../../utils/pdfcanvasutils '
import { PdfEsignUltil } from './components/PDFMerge'
import {
  isDelegateFeedbackTab,
  toDisplayDocumentNumber,
} from './feedbackBoardType'

const FeedbackCommentSection = props => {
  const {
    selectedIndex,
    feedbackCommentDataDetailV2,
    currentUser,
    departmentWithUserListv2,
    fileStore,
    FeedbackCommentStore,
    loadingAnimationStore,
    EDITOR_REF,
    updateCurrentComment,
    documentNumber,
    voterName,
    feedbackTab,
  } = props

  const [form] = Form.useForm()
  const [showDeletedComments, setShowDeletedComments] = useState(false)
  const [uploadedFiles, setUploadedFiles] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isCommentListOpen, setIsCommentListOpen] = useState(true)
  const [editorContent, setEditorContent] = useState(null)

  const handleUploadChange = info => {
    const fileList = info.fileList.map(file => ({
      uid: file.uid,
      name: file.name,
      status: file.status,
      originFileObj: file.originFileObj,
    }))
    setUploadedFiles(fileList)
  }

  const removeFile = fileUid => {
    setUploadedFiles(uploadedFiles.filter(f => f.uid !== fileUid))
  }

  const capitalize = str => {
    return str
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const getNameUppercaseByUsername = username => {
    for (const dept of departmentWithUserListv2 || []) {
      const user = dept.users?.find(u => u.user_name === username)
      if (user?.name_uppercase) return capitalize(user.name_uppercase)
    }
    return 'Admin'
  }

  const normalizeFileIds = fileIds => {
    if (!fileIds) return []
    if (Array.isArray(fileIds)) return fileIds
    if (typeof fileIds === 'string') {
      try {
        const parsed = JSON.parse(fileIds)
        return Array.isArray(parsed) ? parsed : [fileIds]
      } catch {
        return fileIds.includes(',')
          ? fileIds.split(',').map(id => id.trim())
          : [fileIds]
      }
    }
    return []
  }

  const normalizeFileNames = fileNames => {
    if (!fileNames) return []
    if (Array.isArray(fileNames)) return fileNames
    if (typeof fileNames === 'string') {
      return fileNames.split(',').map(name => name.trim())
    }
    return []
  }

  const handleDeleteComment = async commentId => {
    try {
      loadingAnimationStore.showSpinner(true)
      await FeedbackCommentStore.deleteComment(commentId)
      message.success('Đã xóa ý kiến thành công!')
      await FeedbackCommentStore.getFeedbackCommentById(
        feedbackCommentDataDetailV2.id
      )
    } catch (error) {
      message.error('Có lỗi xảy ra khi xóa ý kiến!')
      console.error(error)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }

  const handleCommentSubmit = async () => {
    try {
      await form.validateFields()
      setIsSubmitting(true)

      const newComment = EDITOR_REF.current?.editor?.getData() || ''

      if (!newComment.trim()) {
        message.error('Vui lòng nhập nội dung ý kiến!')
        setIsSubmitting(false)
        return
      }

      let uploadFileIds = []
      let uploadFileNames = []

      if (uploadedFiles.length > 0) {
        for (const file of uploadedFiles) {
          if (file.originFileObj) {
            const formData = new FormData()
            formData.append('file', file.originFileObj)
            const uploadResponse = await fileStore.uploadFile(formData)
            uploadFileIds.push(uploadResponse.data.file_id)
            uploadFileNames.push(uploadResponse.data.file_name)
          }
        }
      }

      const latestVotingRecord = feedbackCommentDataDetailV2.feedbackCommentDetails
        .filter(
          item =>
            item.feedbackUsername === currentUser?.username &&
            item.voteType &&
            item.fileId &&
            !item.isDeleted
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]

      const dataSubmit = {
        feedbackUsername: currentUser?.username,
        detailFeedbackComment: newComment,
        uploadFileId: uploadFileIds,
        uploadFileName: uploadFileNames.join(', '),
      }

      if (latestVotingRecord) {
        // Chỉ lấy comments cũ (chưa xóa) của current user
        const oldComments = feedbackCommentDataDetailV2.feedbackCommentDetails
          .filter(
            c =>
              !c.isDeleted &&
              c.feedbackUsername === currentUser?.username &&
              c.detailFeedbackComment &&
              c.detailFeedbackComment.trim() !== ''
          )
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
          )
          .map(c => c.detailFeedbackComment)

        // Merge: [comments cũ của current user] + [comment mới]
        const mergedComments = [...oldComments, newComment].join('<br/>')

        // SET currentComment để ExportPdfV2 có thể đọc được
        if (updateCurrentComment) {
          updateCurrentComment(mergedComments)
        }

        await new Promise(resolve => setTimeout(resolve, 100))

        const ts = new Date().getTime()
        const filePart1 = await renderElementToPdfFile(
          'divToPrintPart1',
          `${currentUser?.code}_${ts}_part1`
        )
        const formDataPart1 = new FormData()
        formDataPart1.append('file', filePart1)
        const responseSignDocPart1 = await fileStore.uploadFile(formDataPart1)

        const filePart2 = await renderElementToPdfFile(
          'divToPrintPart2',
          `${currentUser?.code}_${ts}_part2`
        )

        const formDataPart2 = new FormData()
        formDataPart2.append('file', filePart2)
        const responseSignDocPart2 = await fileStore.uploadFile(formDataPart2)

        const pdfFilePrefix = isDelegateFeedbackTab(feedbackTab)
          ? 'PLYK-NĐD'
          : 'PLYK'
        const normalizedDocumentNumber = toDisplayDocumentNumber(documentNumber)
        const pdfFileName = normalizedDocumentNumber
          ? `${pdfFilePrefix}_${normalizedDocumentNumber}_${(
              voterName || ''
            ).replace(/\s+/g, '_')}`
          : `Bieu_quyet_${new Date().getTime()}`

        const mergedResult = await PdfEsignUltil.mergeCurrentPdfWithGenerated(
          responseSignDocPart1.data.file_id,
          responseSignDocPart2.data.file_id,
          pdfFileName
        )

        dataSubmit.voteType = latestVotingRecord.voteType
        dataSubmit.fileId = mergedResult.file_id
        dataSubmit.fileName = mergedResult.file_name

        await FeedbackCommentStore.updateComment(
          dataSubmit,
          feedbackCommentDataDetailV2?.id
        )
      } else {
        await FeedbackCommentStore.updateComment(
          dataSubmit,
          feedbackCommentDataDetailV2?.id
        )
      }

      setEditorContent(null)
      if (EDITOR_REF.current?.editor) {
        EDITOR_REF.current.editor.setData('')
      }
      form.resetFields()
      setUploadedFiles([])

      message.success('Đã thêm ý kiến!')

      await FeedbackCommentStore.getFeedbackCommentById(
        feedbackCommentDataDetailV2.id
      )
    } catch (err) {
      console.error('Error in submitting comment:', err)
      message.error('Có lỗi xảy ra khi lưu ý kiến!')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!selectedIndex || !feedbackCommentDataDetailV2?.feedbackCommentDetails) {
    return null
  }

  const displayComments = feedbackCommentDataDetailV2.feedbackCommentDetails
    .filter(comment => {
      const hasContent =
        comment.detailFeedbackComment &&
        comment.detailFeedbackComment.trim() !== ''
      const shouldShowDeleted = showDeletedComments || !comment.isDeleted
      return hasContent && shouldShowDeleted
    })
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))

  return (
    <ContentBlockWrapper style={{ marginBottom: 20 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontWeight: 500,
          fontSize: 14,
          margin: '-16px -16px 10px -16px',
          borderTopLeftRadius: 6,
          borderTopRightRadius: 6,
          padding: '10px 16px',
          color: '#fff',
          backgroundColor: '#2C65AC',
          cursor: 'pointer',
        }}
        onClick={() => setIsCommentListOpen(!isCommentListOpen)}>
        <span>Danh sách ý kiến</span>
        <div
          style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
          onClick={e => e.stopPropagation()}>
          <Switch
            size="small"
            checked={showDeletedComments}
            onChange={checked => setShowDeletedComments(checked)}
          />
          <span style={{ fontSize: '12px', color: '#fff', cursor: 'default' }}>
            Hiển thị ý kiến đã xóa
          </span>
          <div onClick={() => setIsCommentListOpen(!isCommentListOpen)}>
            {isCommentListOpen ? <CaretDownFilled /> : <CaretLeftFilled />}
          </div>
        </div>
      </div>

      <div
        style={{
          overflow: 'hidden',
          maxHeight: isCommentListOpen ? '2500px' : '0',
          opacity: isCommentListOpen ? 1 : 0,
          transition:
            'max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.4s ease',
        }}>
        <div
          style={{
            maxHeight: '500px',
            overflow: 'auto',
            marginBottom: '10px',
            paddingRight: '10px',
          }}>
          {displayComments.length > 0 ? (
            displayComments.map(comment => {
              const fileIds = normalizeFileIds(comment.uploadFileId)
              const fileNames = normalizeFileNames(comment.uploadFileName)

              return (
                <div
                  key={comment.id}
                  style={{
                    marginBottom: '10px',
                    padding: '5px 0',
                    borderBottom: '1px solid #f0f0f0',
                    opacity: comment.isDeleted ? 0.5 : 1,
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <span style={{ fontWeight: 'bold' }}>
                        {getNameUppercaseByUsername(comment.feedbackUsername)}:
                      </span>
                      {comment.isDeleted && (
                        <Tag
                          color="red"
                          style={{ fontSize: '10px', margin: 0 }}>
                          Đã xóa
                        </Tag>
                      )}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}>
                      <span style={{ color: 'gray', fontSize: '12px' }}>
                        {new Date(comment.createdAt).toLocaleDateString(
                          'vi-VN',
                          {
                            weekday: 'long',
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                          }
                        )}{' '}
                        {new Date(comment.createdAt).toLocaleTimeString(
                          'vi-VN',
                          { hour: '2-digit', minute: '2-digit' }
                        )}
                      </span>
                      {comment.feedbackUsername === currentUser?.username &&
                        !comment.isDeleted && (
                          <Button
                            type="link"
                            size="small"
                            danger
                            style={{
                              padding: '0',
                              fontSize: '12px',
                              height: 'auto',
                            }}
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteComment(comment.id)}
                          />
                        )}
                    </div>
                  </div>

                  <div
                    style={{ margin: 0, color: '#000', paddingLeft: '4px' }}
                    dangerouslySetInnerHTML={{
                      __html: comment.detailFeedbackComment,
                    }}
                  />

                  {fileIds.length > 0 && (
                    <div
                      style={{
                        marginTop: '8px',
                        paddingTop: '8px',
                        paddingLeft: '4px',
                      }}>
                      {fileIds.map((fileId, index) => {
                        const fileName = fileNames[index] || `File ${index + 1}`
                        return (
                          <FileItem
                            key={`${fileId}-${index}`}
                            file_id={fileId}
                            file_name={fileName}
                            file_type={utils.getExtensionFile(fileName)}
                            style={{ marginBottom: '4px' }}
                          />
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })
          ) : (
            <div
              style={{
                textAlign: 'center',
                color: '#999',
                padding: '20px',
                fontStyle: 'italic',
              }}>
              Chưa có ý kiến nào.
            </div>
          )}
        </div>

        <div
          style={{
            borderTop: '1px solid #e8e8e8',
            paddingTop: '16px',
            paddingBottom: '16px',
          }}>
          <Form form={form} name="feedback-comment-new" layout="vertical">
            <Form.Item name="comment" style={{ marginBottom: '12px' }}>
              <RichEditor
                EDITOR_REF={EDITOR_REF}
                editorContent={editorContent}
                height={150}
                placeholder="Nhập nội dung ý kiến..."
                onEditorChange={content =>
                  form.setFieldsValue({ comment: content })
                }
              />
            </Form.Item>

            {uploadedFiles.length > 0 && (
              <div style={{ marginBottom: '12px' }}>
                {uploadedFiles.map(file => (
                  <div
                    key={file.uid}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '6px',
                      marginBottom: '4px',
                    }}>
                    <span style={{ fontSize: '13px', color: '#333' }}>
                      📎 {file.name}
                    </span>
                    <Button
                      type="link"
                      size="small"
                      danger
                      style={{ padding: 0, height: 'auto' }}
                      onClick={() => removeFile(file.uid)}>
                      Xóa
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                alignItems: 'center',
              }}>
              <Upload
                fileList={uploadedFiles}
                onChange={handleUploadChange}
                beforeUpload={() => false}
                multiple
                maxCount={5}
                showUploadList={false}>
                <Button icon={<UploadOutlined />}>
                  Tải file{' '}
                  {uploadedFiles.length > 0 && `(${uploadedFiles.length})`}
                </Button>
              </Upload>
              <Button
                type="primary"
                onClick={handleCommentSubmit}
                loading={isSubmitting}>
                Đăng ý kiến
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </ContentBlockWrapper>
  )
}

export default inject(
  'FeedbackCommentStore',
  'fileStore',
  'loadingAnimationStore'
)(observer(FeedbackCommentSection))
